/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import '@szhsin/react-menu/dist/index.css'
import 'animate.css'
import 'react-toastify/dist/ReactToastify.css'
import { SimpleInterpolation, createGlobalStyle, css } from 'styled-components'

import type {
  MediaQuery,
  MediaScreenType,
  ThemeColors,
  ThemeShadows,
  ThemeUnits
} from '../types/theme'

export const colors = (darkMode: boolean): ThemeColors => ({
  baseWhite: '#FFF',
  baseBlack: '#000',

  bg: darkMode ? '#FFF' : '#FFF',

  primary300: darkMode ? '#FFB359' : '#FFB359',
  primary: darkMode ? '#FF8A00' : '#FF8A00',
  primary500: darkMode ? '#E98F39' : '#E98F39',
  primary600: darkMode ? '#DBA369' : '#DBA369',

  gray200: darkMode ? '#EDEDED' : '#EDEDED',
  gray300: darkMode ? '#C5C8CD' : '#C5C8CD',
  gray: darkMode ? '#A7A7A7' : '#A7A7A7',
  gray500: darkMode ? '#969696' : '#969696',
  gray600: darkMode ? '#7D7D7D' : '#7D7D7D',
  gray700: darkMode ? '#5F5F5F' : '#5F5F5F',

  red: darkMode ? '#E54358' : '#E54358'
})

export const units: ThemeUnits = {
  borderRadius: {
    small: '7.5px',
    medium: '10px'
  },
  buttonSize: {
    small: '40px',
    medium: '44px',
    large: '50px'
  },
  fontSize: {
    header1: '70px',
    header2: '40px',
    header3: '30px',
    title: '24px',
    body1: '22px',
    body2: '20px',
    body3: '18px',
    regular: '16px'
  }
}

export const shadows = (darkMode: boolean): ThemeShadows => ({
  card: `2px 2px 4px ${darkMode ? 'rgba(148, 148, 148, .1)' : 'rgba(148, 148, 148, .1)'}`,
  footer: `0 -4px 4px ${darkMode ? 'rgba(178, 178, 178, .25)' : 'rgba(178, 178, 178, .25)'}`,
  modal: `2px 2px 4px ${darkMode ? 'rgba(148, 148, 148, .1)' : 'rgba(148, 148, 148, .1)'}`,
  topNavBar: `0 4px 4px ${darkMode ? 'rgba(179,179,179, .25)' : 'rgba(179,179,179, .25)'}`
})

export const MEDIA_MAX_WIDTH: Record<MediaScreenType, number> = {
  mobile: 1023,
  desktop: 3840
}

export const MEDIA_QUERY_RULE: Record<MediaScreenType, string> = {
  mobile: `screen and (max-width: ${MEDIA_MAX_WIDTH.mobile}px)`,
  desktop: `screen and (min-width: ${MEDIA_MAX_WIDTH.mobile + 1}px) and (max-width: ${
    MEDIA_MAX_WIDTH.desktop
  }px)`
}

export const mediaQuery = Object.entries(MEDIA_QUERY_RULE).reduce(
  (templates: Partial<MediaQuery>, [screenType, rule]) => {
    const _templates = {
      ...templates,
      [screenType]: (
        templateString: TemplateStringsArray,
        ...interpolations: SimpleInterpolation[]
      ) => css`
        @media ${rule} {
          ${css(templateString, ...interpolations)}
        }
      `
    }

    return _templates
  },
  {}
) as MediaQuery

export const GlobalThemeStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
  }

  html {
    font-size: ${units.fontSize.regular};
    font-variant: none;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

    // windows chrome will calculate the scroll bar into the viewport width
    // that means when you use 100vw, the actual width is 100% viewport width + scroll width
    // then your layer will overflow, so we need to hide the scroll bar
    &::-webkit-scrollbar {
      display: none;
    }
  }

  html, input, textarea, button {
    font-family: 'Roboto', sans-serif;
  }

  body {
    min-height: 100vh;

    &.modal-open {
      overflow: hidden;
    }
  }

  a {
    text-decoration: none;

    &:visited {
      color: inherit;
    }
  }
`
